import { useCallback, useEffect, useMemo, useState } from 'react';

import { useThree } from '@react-three/fiber';
import * as THREE from 'three';

import { useAppSelector } from '../../../hooks.ts';
import { useStore } from '../../../pages/workspace/hooks.ts';
import { getUser } from '../../../store/selectors/user.selector.ts';
import { MeasurementLine } from './MeasurementLine.tsx';
import { useSceneInteraction } from './useSceneInteraction.ts';

/**
 * Always-mounted pointer handler for the workspace canvas.
 * Scene.tsx is unmounted in measurement results view; this component keeps
 * right-click quick-edit and measurement-tool interaction available (except
 * luminaire quick-edit in real-time render, where lamps are not shown).
 */
export function SceneInteractionHandler() {
  const {
    lamps,
    setHoveredLayer,
    selectLamp,
    isMeasurementMode,
    isLampCreationMode,
    isLampDistanceMode,
    measurementPoints,
    addMeasurementPoint,
    clearMeasurementPoints,
    activeViewModes,
  } = useStore();

  const user = useAppSelector(getUser)!;
  const unitSystem = user.settings.units;

  // While drawing/cutting a measurement surface, the surface editor owns the canvas —
  // suppress layer hover/select and quick-edit so clicks don't hit the model behind it.
  const surfaceDrawing = useAppSelector(
    (s) => s.measurementPlanes.draw.active || s.measurementPlanes.exclusionDraw.active,
  );

  const { camera, scene } = useThree();

  const [localMeasurementPoints, setLocalMeasurementPoints] = useState<THREE.Vector3[]>([]);
  const [localHoverPoint, setLocalHoverPoint] = useState<THREE.Vector3 | null>(null);
  const [isHoverSnapped, setIsHoverSnapped] = useState(false);

  const measurementRaycaster = useMemo(() => new THREE.Raycaster(), []);
  const groundPlane = useMemo(() => new THREE.Plane(new THREE.Vector3(0, 0, 1), 0), []);

  useEffect(() => {
    if (measurementPoints.length === 0) {
      setLocalMeasurementPoints([]);
      setLocalHoverPoint(null);
      setIsHoverSnapped(false);
    }
  }, [measurementPoints]);

  useEffect(() => {
    if (!isMeasurementMode) {
      setLocalMeasurementPoints([]);
      setLocalHoverPoint(null);
      setIsHoverSnapped(false);
    }
  }, [isMeasurementMode]);

  const getMeasurableObjects = useCallback((): THREE.Object3D[] => {
    const measurableObjects: THREE.Object3D[] = [];
    scene.traverse((object) => {
      if (
        (object as THREE.Mesh).isMesh &&
        object.visible &&
        !object.userData.isMeasurement &&
        (object.userData.layerName || object.userData.type === 'lamp')
      ) {
        measurableObjects.push(object);
      }
    });
    return measurableObjects;
  }, [scene]);

  const getIntersectionPoint = useCallback(
    (
      mouse: THREE.Vector2,
    ): {
      point: THREE.Vector3;
      isLamp: boolean;
      lampId?: string;
    } | null => {
      camera.updateMatrixWorld();
      measurementRaycaster.setFromCamera(mouse, camera);

      const measurableObjects = getMeasurableObjects();

      if (measurableObjects.length === 0) {
        const pt = new THREE.Vector3();
        const hit = measurementRaycaster.ray.intersectPlane(groundPlane, pt);
        return hit ? { point: pt, isLamp: false } : null;
      }

      const intersects = measurementRaycaster.intersectObjects(measurableObjects, false);
      if (intersects.length > 0) {
        const firstHit = intersects[0];

        let lampGroup: THREE.Object3D | null = firstHit.object;
        while (lampGroup && lampGroup.userData.type !== 'lamp') {
          lampGroup = lampGroup.parent;
        }
        if (lampGroup?.userData.id) {
          const lampId = lampGroup.userData.id as string;
          const lamp = lamps.find((l) => l.id === lampId);
          if (lamp) {
            return {
              point: new THREE.Vector3(lamp.position[0], lamp.position[1], 0),
              isLamp: true,
              lampId,
            };
          }
        }

        return { point: firstHit.point.clone(), isLamp: false };
      }

      const pt = new THREE.Vector3();
      const hit = measurementRaycaster.ray.intersectPlane(groundPlane, pt);
      return hit ? { point: pt, isLamp: false } : null;
    },
    [camera, getMeasurableObjects, groundPlane, lamps, measurementRaycaster],
  );

  const addLocalPoint = useCallback((pt: THREE.Vector3) => {
    setLocalMeasurementPoints((prev) => [...prev, pt]);
  }, []);

  const clearLocalPoints = useCallback(() => {
    setLocalMeasurementPoints([]);
    setLocalHoverPoint(null);
    setIsHoverSnapped(false);
  }, []);

  const measurementActions = useMemo(
    () => ({
      addPoint: addLocalPoint,
      clearPoints: clearLocalPoints,
      setHoverPoint: setLocalHoverPoint,
      setIsHoverSnapped,
      addMeasurementPoint,
      clearMeasurementPoints,
    }),
    [addLocalPoint, clearLocalPoints, addMeasurementPoint, clearMeasurementPoints],
  );

  useSceneInteraction({
    isMeasurementActive: isMeasurementMode,
    isLampCreationMode,
    isLampDistanceMode,
    measurementPoints: localMeasurementPoints,
    localHoverPoint,
    measurementActions,
    setHoveredLayer,
    selectLamp,
    getIntersectionPoint,
    isLampQuickEditDisabled: activeViewModes.realTimeRender,
    disabled: surfaceDrawing,
  });

  const displayPoints: [number, number, number][] = useMemo(() => {
    return localMeasurementPoints.map((p) => [p.x, p.y, p.z] as [number, number, number]);
  }, [localMeasurementPoints]);

  const displayDistance = useMemo(() => {
    if (localMeasurementPoints.length < 2) return null;
    let total = 0;
    for (let i = 1; i < localMeasurementPoints.length; i++) {
      total += localMeasurementPoints[i - 1].distanceTo(localMeasurementPoints[i]);
    }
    return total;
  }, [localMeasurementPoints]);

  const displaySegmentDistances = useMemo(() => {
    const segs: number[] = [];
    for (let i = 1; i < localMeasurementPoints.length; i++) {
      segs.push(localMeasurementPoints[i - 1].distanceTo(localMeasurementPoints[i]));
    }
    return segs;
  }, [localMeasurementPoints]);

  const displayHoverPoint: [number, number, number] | null = useMemo(() => {
    return localHoverPoint ? [localHoverPoint.x, localHoverPoint.y, localHoverPoint.z] : null;
  }, [localHoverPoint]);

  if (!isMeasurementMode) return null;

  return (
    <MeasurementLine
      points={displayPoints}
      distance={displayDistance}
      segmentDistances={displaySegmentDistances}
      hoverPoint={displayHoverPoint}
      isHoverSnapped={isHoverSnapped}
      showCtrlHint={displayPoints.length === 0 && !displayHoverPoint}
      unitSystem={unitSystem}
    />
  );
}
